import React, { useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { fetchPaymentIntent } from 'store/actions/order'
import * as appActions from 'store/actions/app'

// Stripe.js collects "advanced fraud signals" that feed its bot detection, which
// throws an hCaptcha challenge at confirmPayment when a session looks automated —
// exactly what QA's e2e runs look like (headless browser, same card/amount/IP in
// rapid succession). Disable the signals whenever we run on a TEST-mode key
// (pk_test_ = localhost/dev/staging) so automated runs don't get challenged;
// live keys (production) keep full fraud-signal collection. Must run before the
// first loadStripe() call; the setLoadParameters guard keeps us safe on older
// @stripe/stripe-js versions and under test mocks.
if (
  (process.env.REACT_APP_STRIPE_PUB_KEY || '').startsWith('pk_test_') &&
  typeof loadStripe.setLoadParameters === 'function'
) {
  loadStripe.setLoadParameters({ advancedFraudSignals: false })
}

// Deferred intent creation (BOTS-37): the PaymentIntent is created server-side only when
// the borrower submits — not on page load — so borrowers who load the page and leave don't
// leave abandoned PaymentIntents. The Element is mounted with mode/amount/currency instead
// of a client_secret; confirmPayment is given the freshly-created client_secret at submit.
const PaymentForm = ({ token, orderId, onSuccess }) => {
  const stripe = useStripe()
  const elements = useElements()
  const dispatch = useDispatch()
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!stripe || !elements || submitting) { return }

    setSubmitting(true)

    // 1. Validate the Payment Element before creating anything server-side.
    const { error: submitError } = await elements.submit()
    if (submitError) {
      setSubmitting(false)
      dispatch(appActions.appErrors([submitError.message]))
      return
    }

    // 2. Create the PaymentIntent now (deferred). fetchPaymentIntent dispatches its own
    //    error + throws on failure, so just abort here.
    let clientSecret
    try {
      const intent = await dispatch(fetchPaymentIntent(token, orderId))
      clientSecret = intent.client_secret
    } catch (err) {
      setSubmitting(false)
      return
    }

    // 3. Confirm the freshly-created PI with the collected payment details.
    // confirmPayment can also *throw* (e.g. an IntegrationError) rather than resolve with
    // { error }; without catching it, the throw would skip setSubmitting(false) and strand
    // the button on "Processing...". Catch so we always reset and surface a message.
    let error
    try {
      ({ error } = await stripe.confirmPayment({
        elements,
        clientSecret,
        // Deferred Elements + automatic_payment_methods (which allows redirects) requires a
        // return_url even with redirect: 'if_required'. Cards/Link confirm inline and never
        // use it; it's only hit if a redirect-based method is ever enabled (BOTS-30).
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      }))
    } catch (err) {
      error = err
    }
    setSubmitting(false)

    if (error) {
      dispatch(appActions.appErrors([error.message || 'Unable to process payment. Please try again.']))
    } else {
      onSuccess()
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      <div
        className={submitting || !stripe ? 'button-disabled' : 'button'}
        onClick={!submitting && stripe ? handleSubmit : undefined}
        style={{ marginTop: '16px' }}
      >
        {submitting ? 'Processing...' : 'Submit Payment'}
      </div>
    </form>
  )
}

const BorrowerPaymentForm = ({ activePayment, changeStep }) => {
  const token = useSelector(state => state.app.token)
  const orderId = useSelector(state => state.order.id)
  const [stripe, setStripe] = useState(null)
  const [initError, setInitError] = useState(false)

  // Stripe.js is fetched from js.stripe.com at runtime; on a flaky connection or with a
  // content blocker it can fail, which previously left the borrower on a blank step (and the
  // failure as an unhandled rejection — Datadog issue edc962ba). Treat it as a recoverable
  // state with a "Try again" button. loadStripe() resets its internal promise and re-injects
  // the script on failure, so retrying it is a genuine reload. The PaymentIntent is NOT
  // created here (deferred to submit — BOTS-37), so there is nothing PI-related to retry.
  const initialize = useCallback(() => {
    setInitError(false)
    if (!stripe) {
      loadStripe(process.env.REACT_APP_STRIPE_PUB_KEY)
        .then((stripeInstance) => {
          if (!stripeInstance) { throw new Error('Stripe.js unavailable') }
          setStripe(stripeInstance)
        })
        .catch(() => setInitError(true))
    }
  }, [stripe])

  // Mount-once.
  useEffect(() => {
    initialize()
  }, [])

  if (initError) {
    return (
      <div className="flex-container text-center">
        <div className="box fullwidth">
          <p style={{ marginBottom: '16px' }}>
            We were unable to load the payment form. Please check your connection and try again.
          </p>
          <div className="button" onClick={initialize} style={{ marginTop: '16px' }}>
            Try again
          </div>
        </div>
      </div>
    )
  }

  if (!stripe) { return null }

  // amount is in cents and must match the server-side PaymentIntent amount
  // (create_payment_intent uses payment.amount, i.e. activePayment.amount). Stripe's
  // elements() requires a positive integer in the currency subunit and type-checks it,
  // but activePayment.amount can arrive as a string — so coerce to an int.
  const options = {
    mode: 'payment',
    amount: parseInt(activePayment.amount, 10),
    currency: 'usd',
  }

  return (
    <div className="flex-container text-center">
      <div className="box fullwidth">
        <p style={{ marginBottom: '16px' }}>
          Your payment is <b>${(activePayment.up_to_amount
          ? activePayment.up_to_amount
          : activePayment.amount / 100).toFixed(2)}</b>
          <br />
          All payments must be made before proceeding with the appraisal
        </p>
        <Elements stripe={stripe} options={options}>
          <PaymentForm token={token} orderId={orderId} onSuccess={changeStep} />
        </Elements>
      </div>
    </div>
  )
}

export default BorrowerPaymentForm
